import { useEffect, useRef, useState } from "react";
import { inboxServices } from "../../APIServices/SitePilotManagement/inboxServices";
import { FaPencil, FaXmark, FaPhone, FaVideo } from "react-icons/fa6";
import { CgSmileMouthOpen } from "react-icons/cg";
import Picker from "@emoji-mart/react";
import { Data } from "emoji-mart";
import AudioCallPopups from "../../CallPopups/AudioCallPopups";
import ReceivedAudioCallPopups from "../../CallPopups/ReceivedAudioCallPopups";
import ReceivedVideoCallPopus from "../../CallPopups/ReceivedVideoCallPopus";
import VideoCallOutgoingPopups from "../../CallPopups/VideoCallOutgoingPopups";
import VideoCallScreenDesign from "../../CallPopups/VideoCallScreenDesign";
import "./LiveChat.css";

const LiveChat = ({
  setShowChat,
  themeColor,
  messages,
  propertyId,
  visitorInfo,
  visitorData,
  messageLoader,
  setVisitorInfo,
  addNewInbox,
  wsRef,
  emitTypingPreview,
  visitorType,
  inputMsg,
  setInputMsg,
  isTyping,
  sendNotification,
  setVisitorType,
  updateInboxSession,
  widgetDetails,
  callManager,
  videoCallManager,
  onAudioCallClick,
  onVideoCallClick,
  adminUserId = "admin",
  adminName = "Admin",
}) => {
  const [isOnline, setIsOnline] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const [showEmoji, setShowEmoji] = useState(false);

  const messagesEndRef = useRef(null);
  const typingSendTimerRef = useRef(null);
  const typingStopTimerRef = useRef(null);
  const inputRef = useRef(null);

  const handleCloseChat = () => {
    setShowChat(false);
    sendNotification("notification", "Visitor Closed the Chat Interface.");
  };

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleVisitorInfoChange = (index, field, value) => {
    const updated = [...visitorInfo];
    updated[index][field] = value;
    setVisitorInfo(updated);
  };

  const hasEmptyAdditionalInfo = () => {
    return visitorInfo.some(
      (item) =>
        item.required === "yes" &&
        (!item.label?.trim() || !item.value?.trim())
    );
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    return () => {
      clearTimeout(typingSendTimerRef.current);
      clearTimeout(typingStopTimerRef.current);
    };
  }, []);

  const sendMessage = async (inboxId) => {
    const text = String(inputMsg || "").trim();
    if (!text || !wsRef.current) return;

    emitTypingPreview("");

    const ok = wsRef.current.sendRoomMessage({
      propertyId,
      sessionId: visitorData?.sessionId,
      text,
      visitorId: inboxId,
      userId: inboxId,
      userIcon: visitorData?.icon,
      userName: visitorData?.name || inboxId,
      userType: "visitor",
      type: "message",
    });

    if (ok) {
      const data = await inboxServices.sendInboxMessage({
        inboxId,
        sessionId: visitorData?.sessionId,
        userId: inboxId,
        userIcon: visitorData?.icon,
        userName: visitorData?.name || inboxId,
        message: text,
        type: "message",
        userType: "visitor",
      });

      if (data?.statusCode === 200) {
        setInputMsg("");
      }
    }
  };

  const handlePhoneMouseDown = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
  };

  const makeAudioCall = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (typeof onAudioCallClick === "function") {
      const result = await onAudioCallClick();
      if (!result?.ok && result?.reason) {
        console.error("Audio call could not start:", result.reason);
      }
    } else {
      console.error("onAudioCallClick handler missing");
    }
  };

  const makeVideoCall = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (typeof onVideoCallClick === "function") {
      const result = await onVideoCallClick();
      if (!result?.ok && result?.reason) {
        console.error("Video call could not start:", result.reason);
      }
    } else {
      console.error("onVideoCallClick handler missing");
    }
  };

  const iconButtonStyle = {
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    color: "inherit",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    pointerEvents: "auto",
    zIndex: 5,
  };

  return (
    <>
      <div
        className="chat-wrapper"
        style={{
          bottom: isMobile ? 0 : 24,
          right: isMobile ? 0 : 24,
          width: isMobile ? "100%" : 340,
          height: isMobile ? "100%" : 420,
          borderRadius: isMobile ? 0 : 16,
        }}
      >
        <div className="chat-header" style={{ background: themeColor }}>
          <div className="w-100">
            <div className="header_top">
              <div>
                Chat with Agent
                <span className="chat-status">
                  ({isOnline ? "Online" : "Offline"})
                </span>
              </div>

              <div className="chat-icons">
                {widgetDetails?.widgetVoiceAndVideoCall && (
                  <>
                    <button
                      type="button"
                      title="Make Call"
                      aria-label="Make Call"
                      onMouseDown={handlePhoneMouseDown}
                      onClick={makeAudioCall}
                      style={iconButtonStyle}
                    >
                      <FaPhone style={{ fontSize: 13 }} />
                    </button>

                    <button
                      type="button"
                      title="Make Video Call"
                      aria-label="Make Video Call"
                      onMouseDown={handlePhoneMouseDown}
                      onClick={makeVideoCall}
                      style={iconButtonStyle}
                    >
                      <FaVideo />
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={handleCloseChat}
                  title="Close"
                  aria-label="Close"
                  style={iconButtonStyle}
                >
                  <FaXmark />
                </button>
              </div>
            </div>

            {visitorData?.type === "inbox" && visitorType !== "visitorForm" && (
              <div className="welcome-row">
                Welcome to {visitorData?.name}
                <span
                  className="chatBadge"
                  onClick={() => setVisitorType("visitorForm")}
                >
                  Not you?
                </span>
                {messages?.length > 0 && (
                  <span
                    className="chatBadge"
                    onClick={() => updateInboxSession()}
                  >
                    New Chat
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="chat-body">
          {visitorType !== "visitorForm" ? (
            <>
              {messages?.length > 0 ? (
                messages.map((msg, i) => {
                  const isUser = msg.userType === "visitor";

                  return (
                    <div
                      key={msg?.messageId || `${msg?.createdAt || ""}-${i}`}
                      className={`chat-message ${isUser ? "user" : "agent"}`}
                    >
                      {!isUser && (
                        <img
                          src={msg?.userIcon}
                          alt=""
                          className="chat-avatar"
                        />
                      )}

                      <div
                        className="chat-bubble"
                        style={{
                          background: isUser ? themeColor : "#e9ecef",
                          color: isUser ? "#fff" : "#000",
                        }}
                      >
                        {msg.message}
                        <div className="chat-time">
                          {formatTime(msg.createdAt)}
                        </div>
                      </div>

                      {isUser && (
                        <img
                          src={msg?.userIcon}
                          alt=""
                          className="chat-avatar"
                        />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="no-message">No any Message..</div>
              )}

              {isTyping && (
                <div className="chat-message agent">
                  <div className="typing-box">
                    Agent Typing...
                    <FaPencil />
                  </div>
                </div>
              )}

              {!!callManager?.error && (
                <div className="chat-message agent">
                  <div className="typing-box" style={{ color: "#b42318" }}>
                    {callManager.error}
                  </div>
                </div>
              )}

              {!!videoCallManager?.error && (
                <div className="chat-message agent">
                  <div className="typing-box" style={{ color: "#b42318" }}>
                    {videoCallManager.error}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          ) : (
            <div className="visitor-form">
              {visitorInfo?.map((item, index) => (
                <div key={index} className="form-group">
                  <label>
                    {item?.label}
                    {item.required === "yes" && (
                      <span className="required">*</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={item?.value}
                    onChange={(e) =>
                      handleVisitorInfoChange(index, "value", e.target.value)
                    }
                    placeholder={`Enter ${item?.label}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {visitorType === "inbox" ? (
          <div className="chat-footer">
            <textarea
              ref={inputRef}
              value={inputMsg}
              onChange={(e) => {
                const val = e.target.value;
                setInputMsg(val);

                clearTimeout(typingSendTimerRef.current);
                typingSendTimerRef.current = setTimeout(() => {
                  emitTypingPreview(val);
                }, 120);

                clearTimeout(typingStopTimerRef.current);
                typingStopTimerRef.current = setTimeout(() => {
                  emitTypingPreview("");
                }, 3500);
              }}
              placeholder="Type a message..."
              rows={1}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  sendMessage(visitorData?.inboxId);
                }
              }}
            />

            {!widgetDetails?.featureSettings?.disableEmojiReaction && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEmoji((prev) => !prev);
                }}
                style={{ background: themeColor }}
              >
                <CgSmileMouthOpen />
              </button>
            )}

            <button
              onClick={() => sendMessage(visitorData?.inboxId)}
              style={{ background: themeColor }}
            >
              Send
            </button>

            {showEmoji && (
              <div className="emoji-picker">
                <Picker
                  data={Data}
                  theme="light"
                  onEmojiSelect={(emoji) =>
                    setInputMsg((prev) => prev + emoji.native)
                  }
                />
              </div>
            )}
          </div>
        ) : visitorType === "visitorForm" ? (
          <div className="chat-footer">
            <button
              className="full-btn"
              disabled={hasEmptyAdditionalInfo() || messageLoader}
              onClick={() => {
                addNewInbox();
              }}
              style={{ background: themeColor }}
            >
              {messageLoader ? "Submitting..." : "Submit"}
            </button>
          </div>
        ) : (
          <div className="chat-footer">
            <button
              className="full-btn"
              onClick={() => setVisitorType("visitorForm")}
              style={{ background: themeColor }}
            >
              Start Conversation
            </button>
          </div>
        )}
      </div>

      {callManager && (
        <audio ref={callManager.remoteAudioRef} autoPlay playsInline />
      )}

      {callManager && (
        <AudioCallPopups
          visible={
            callManager.callState === "outgoing" ||
            callManager.callState === "connecting" ||
            callManager.callState === "connected" ||
            callManager.callState === "ended" ||
            !!callManager.error
          }
          title={
            callManager.error
              ? "Audio Call Error"
              : callManager.callState === "outgoing"
                ? `Calling ${adminUserId || "Admin"}...`
                : callManager.callState === "connecting"
                  ? "Connecting..."
                  : callManager.callState === "connected"
                    ? "Audio Call Connected"
                    : "Call Ended"
          }
          subTitle={
            callManager.error
              ? callManager.error
              : callManager.callState === "connected"
                ? `You are now talking with ${adminUserId || "admin"}`
                : visitorData?.name || visitorData?.inboxId || ""
          }
          onCancel={callManager.endCall}
        />
      )}

      {callManager && (
        <ReceivedAudioCallPopups
          visible={callManager.callState === "incoming"}
          callerName={
            callManager.incomingCall?.userName ||
            callManager.incomingCall?.from ||
            "Unknown"
          }
          onAccept={callManager.acceptIncomingCall}
          onReject={callManager.rejectIncomingCall}
        />
      )}

      {videoCallManager && (
        <VideoCallOutgoingPopups
          visible={
            videoCallManager.callState === "outgoing" ||
            videoCallManager.callState === "connecting" ||
            !!videoCallManager.error
          }
          title={
            videoCallManager.error
              ? "Video Call Error"
              : videoCallManager.callState === "outgoing"
                ? `Calling ${adminName || adminUserId || "Admin"}...`
                : "Connecting video..."
          }
          subTitle={
            videoCallManager.error ||
            visitorData?.name ||
            visitorData?.inboxId ||
            ""
          }
          onCancel={videoCallManager.endCall}
        />
      )}

      {videoCallManager && (
        <ReceivedVideoCallPopus
          visible={videoCallManager.callState === "incoming"}
          callerName={
            videoCallManager.incomingCall?.userName ||
            videoCallManager.incomingCall?.from ||
            "Unknown"
          }
          onAccept={videoCallManager.acceptIncomingCall}
          onReject={videoCallManager.rejectIncomingCall}
        />
      )}

      {videoCallManager && (
        <VideoCallScreenDesign
          visible={videoCallManager.callState === "connected"}
          localVideoRef={videoCallManager.localVideoRef}
          remoteVideoRef={videoCallManager.remoteVideoRef}
          localName={visitorData?.name || visitorData?.inboxId || "You"}
          remoteName={adminName || adminUserId || "Admin"}
          onEndCall={videoCallManager.endCall}
          isMicEnabled={videoCallManager.isMicEnabled}
          isCameraEnabled={videoCallManager.isCameraEnabled}
          isScreenSharing={videoCallManager.isScreenSharing}
          onToggleMic={videoCallManager.toggleMic}
          onToggleCamera={videoCallManager.toggleCamera}
          onToggleScreenShare={videoCallManager.toggleScreenShare}
        />
      )}
    </>
  );
};

export default LiveChat;